import { characterAvatarUrl } from '@/lib/assetUrl';
import type { Conversation } from '@/hooks/useMessages';
import { useFormatMessageTime } from '@/pages/messages/messageUtils';

interface ConversationListProps {
  conversations: Conversation[];
  activeCharId: number | null;
  isLoading: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  emptyHint?: string;
  onSelect: (conv: Conversation) => void;
  onNewChat: () => void;
  emptyLabel: string;
  loadingLabel: string;
  title: string;
  newChatTitle: string;
}

export function ConversationList({
  conversations,
  activeCharId,
  isLoading,
  loadError,
  onRetry,
  retryLabel,
  emptyHint,
  onSelect,
  onNewChat,
  emptyLabel,
  loadingLabel,
  title,
  newChatTitle,
}: ConversationListProps) {
  const formatTime = useFormatMessageTime();

  return (
    <div className="conversations-sidebar">
      <div className="conversations-header">
        <h3>{title}</h3>
        <button type="button" id="new-chat-btn" className="btn-new-chat" title={newChatTitle} onClick={onNewChat}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      <div className="conversations-list" id="conversations-list">
        {isLoading ? (
          <div className="loading-conversations">{loadingLabel}</div>
        ) : loadError && conversations.length === 0 ? (
          <div className="no-conversations conversations-error">
            <p>{loadError}</p>
            {onRetry ? (
              <button type="button" className="btn-new-chat conversations-retry-btn" onClick={onRetry}>
                {retryLabel ?? 'Retry'}
              </button>
            ) : null}
          </div>
        ) : conversations.length === 0 ? (
          <div className="no-conversations">
            <p>{emptyLabel}</p>
            {emptyHint ? <p className="conversations-empty-hint">{emptyHint}</p> : null}
          </div>
        ) : (
          conversations.flatMap((conv) => {
            const otherId = Number(conv.otherCharID);
            if (!otherId || Number.isNaN(otherId)) return [];

            const isActive = activeCharId === otherId;
            const preview =
              conv.message.length > 40 ? `${conv.message.substring(0, 40)}...` : conv.message;
            const unreadBadge =
              conv.unreadCount > 0 ? (
                <span className="conversation-badge">
                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                </span>
              ) : null;

            return [
              <div
                key={otherId}
                className={`conversation-item${isActive ? ' active' : ''}`}
                data-charid={otherId}
                onClick={() => onSelect(conv)}
                onKeyDown={(e) => {if (e.key === 'Enter' || e.key === ' ') {e.preventDefault(); onSelect(conv);}}}
                aria-pressed={isActive}
                role="button"
                tabIndex={0}
              >
                <img
                  src={characterAvatarUrl(conv.otherRefObjID)}
                  alt=""
                  className="conversation-avatar"
                />
                <div className="conversation-details">
                  <div className="conversation-top">
                    <span className="conversation-name">{conv.otherCharName}</span>
                    <span className="conversation-time">{formatTime(conv.created_at)}</span>
                  </div>
                  <div className="conversation-bottom">
                    <span className="conversation-preview">{preview}</span>
                    {unreadBadge}
                  </div>
                </div>
              </div>,
            ];
          })
        )}
      </div>
    </div>
  );
}
